import { Query } from "appwrite";

import {
  APPWRITE_DATABASE_ID,
  PROJECT_DEVICES_TABLE_ID,
  client,
  tablesDB
} from "src/handlers/Appwrite";
import { listAllRows } from "src/handlers/Appwrite/utils";
import {
  getDefaultRouterTab,
  getDefaultSwitchTab
} from "src/handlers/Projects/schema";
import { ensureSharedDeviceRow } from "src/handlers/ProjectSync";
import { logActivity } from "src/handlers/Activity";
import { summarizeSectionDiff } from "src/handlers/Activity/diff";
import { beginSave } from "src/handlers/syncStatus";

import { checkDeviceServerVersion, mergeServerSection } from "./conflicts";
import { classifyWriteError } from "./fallback";
import {
  ROUTER_TYPE,
  SECTION_KEYS,
  SUPPORTED_DEVICE_TYPES,
  buildDeviceRowData,
  getProjectId,
  makeDeviceRowId,
  parseJson,
  stringify
} from "./internal";
import {
  deletePageRow,
  subscribePageSync,
  syncPageData,
  syncPageDown
} from "./pages";

// ---------------------------------------------------------------------------
// Device-row CRUD with permission-fallback to the project-sync Cloud Function.
//
// `upsertDeviceRow` is the only mutator: every public entry point eventually
// funnels through it. The fallback to `ensureSharedDeviceRow` is preserved
// from the original module \u2014 Phase B (B1) reshapes it into explicit
// 404/401/403 branches.
// ---------------------------------------------------------------------------

export function getEmptyRouterTab(routerNumber) {
  return getDefaultRouterTab(routerNumber);
}

// Phase I1: best-effort read of the server's current section value. Runs
// in parallel with the write so the audit logger can skip no-op saves and
// attach a one-line diff summary. Returns `undefined` when the row doesn't
// exist yet (first save for this device tab) so the caller can distinguish
// "no change" from "brand new".
async function peekServerSectionValue(type, tabId, sectionKey) {
  const projectId = getProjectId();
  if (!projectId) return undefined;
  const rowId = makeDeviceRowId(projectId, type, tabId);
  try {
    const row = await tablesDB.getRow({
      databaseId: APPWRITE_DATABASE_ID,
      tableId: PROJECT_DEVICES_TABLE_ID,
      rowId
    });
    const tab = parseJson(row?.dataJson, null);
    return tab ? tab[sectionKey] : undefined;
  } catch (error) {
    if (error?.code === 404) return undefined;
    // Never let audit plumbing break a write; swallow other errors.
    return undefined;
  }
}

async function upsertDeviceRow(type, tabId, tabData, { expectedAtMs = 0 } = {}) {
  const projectId = getProjectId();
  if (!projectId) return null;
  const rowId = makeDeviceRowId(projectId, type, tabId);

  const conflict = await checkDeviceServerVersion(rowId, expectedAtMs);
  if (conflict.conflict) {
    return {
      _conflict: true,
      serverTab: conflict.serverTab,
      serverTs: conflict.serverTs
    };
  }

  const rowData = buildDeviceRowData(projectId, type, tabId, tabData);

  try {
    return await tablesDB.updateRow({
      databaseId: APPWRITE_DATABASE_ID,
      tableId: PROJECT_DEVICES_TABLE_ID,
      rowId,
      data: rowData
    });
  } catch (error) {
    const decision = classifyWriteError({
      context: "devices.upsert",
      code: error?.code,
      error,
      deviceType: type,
      tabId
    });
    if (decision.action === "throw") throw decision.error;
    // "create" and "fallback" both end up calling the cloud function for
    // now, since `ensureSharedDeviceRow` handles both create-on-missing and
    // ACL-fixup for shared projects. A future refactor can split these
    // into a direct `tablesDB.createRow` for the create path.
  }

  return ensureSharedDeviceRow({
    projectId,
    type,
    tabId,
    tabData
  });
}

// ---------------------------------------------------------------------------
// Public entry points
// ---------------------------------------------------------------------------

export async function syncup(allTabs, type) {
  const projectId = getProjectId();
  if (!projectId) return;

  const end = beginSave();
  try {
    const result = await syncupInternal(allTabs, type, projectId);
    end();
    return result;
  } catch (error) {
    end(error);
    throw error;
  }
}

async function syncupInternal(allTabs, type, projectId) {
  if (type === "vlan") {
    if (Array.isArray(allTabs)) {
      await syncPageData("vlan", allTabs);
    } else {
      await deletePageRow("vlan");
    }
    return;
  }

  if (!SUPPORTED_DEVICE_TYPES.includes(type)) return;

  const existingRows = await listAllRows(tablesDB, {
    databaseId: APPWRITE_DATABASE_ID,
    tableId: PROJECT_DEVICES_TABLE_ID,
    queries: [
      Query.equal("projectId", [projectId]),
      Query.equal("deviceType", [type]),
      Query.orderAsc("deviceId")
    ]
  });

  const rowsByDeviceId = new Map(
    existingRows.map((row) => [Number(row.deviceId), row])
  );

  if (!Array.isArray(allTabs)) {
    await Promise.all(
      existingRows.map((row) =>
        tablesDB.deleteRow({
          databaseId: APPWRITE_DATABASE_ID,
          tableId: PROJECT_DEVICES_TABLE_ID,
          rowId: row.$id
        })
      )
    );
    return;
  }

  await Promise.all(
    allTabs.map(async (tab, tabId) => {
      const existing = rowsByDeviceId.get(tabId);
      const nextDataJson = stringify(tab);
      const nextLabel = tab?.initial?.[0]?.hostname || `${type}-${tabId + 1}`;

      if (existing) {
        if (existing.dataJson === nextDataJson && (existing.label || "") === nextLabel) {
          return;
        }

        await tablesDB.updateRow({
          databaseId: APPWRITE_DATABASE_ID,
          tableId: PROJECT_DEVICES_TABLE_ID,
          rowId: existing.$id,
          data: buildDeviceRowData(projectId, type, tabId, tab)
        });
        return;
      }

      await ensureSharedDeviceRow({
        projectId,
        type,
        tabId,
        tabData: tab
      });
    })
  );

  const nextIds = new Set(allTabs.map((_, index) => index));
  await Promise.all(
    existingRows
      .filter((row) => !nextIds.has(Number(row.deviceId)))
      .map((row) =>
        tablesDB.deleteRow({
          databaseId: APPWRITE_DATABASE_ID,
          tableId: PROJECT_DEVICES_TABLE_ID,
          rowId: row.$id
        })
      )
  );
}

/**
 * Persist a single device-tab section. Used by the editor's per-field save
 * loop. Conflict-aware: if the server row was updated since `expectedAtMs`,
 * the local section is merged on top of the server's tab.
 *
 * @param {number} tabId
 * @param {string} sectionKey   One of the entries in `SECTION_KEYS`.
 * @param {any} sectionValue
 * @param {object|undefined} wholeTab  Full local tab snapshot (for merging).
 * @param {"router" | "switch"} [type]
 * @param {{ expectedAtMs?: number }} [opts]
 * @returns {Promise<import("src/handlers/types").DeviceRow | { _conflict: true } | null | undefined>}
 */
export async function syncSection(
  tabId,
  sectionKey,
  sectionValue,
  wholeTab,
  type = ROUTER_TYPE,
  { expectedAtMs = 0 } = {}
) {
  const projectId = getProjectId();
  if (!projectId) return;

  if (!SUPPORTED_DEVICE_TYPES.includes(type)) {
    throw new Error(`Unsupported device type: ${type}`);
  }

  if (!SECTION_KEYS.includes(sectionKey)) {
    throw new Error(`Unknown device section: ${sectionKey}`);
  }

  const currentTab = {
    ...(wholeTab || (
      type === ROUTER_TYPE ? getDefaultRouterTab(tabId + 1) : getDefaultSwitchTab(tabId + 1)
    ))
  };
  currentTab[sectionKey] = sectionValue;

  // Kick off the pre-read in parallel with the write so the audit diff is
  // ready by the time the write completes. Any failure is swallowed; the
  // audit log is best-effort.
  const previousSectionPromise = peekServerSectionValue(type, tabId, sectionKey);

  const end = beginSave();
  try {
    const result = await upsertDeviceRow(type, tabId, currentTab, { expectedAtMs });

    // If the server had newer data, merge: take the server's tab but apply
    // our changed section on top, then write again without a conflict check.
    if (result?._conflict && result.serverTab) {
      const merged = mergeServerSection(result.serverTab, sectionKey, sectionValue);
      await upsertDeviceRow(type, tabId, merged);
    }

    // Phase H2/I1: fire-and-forget activity log. The audit row is
    // best-effort and never blocks the hot write path. Skip writes that
    // don't change the section value on the server (e.g. tab-switch
    // flushes of already-synced state).
    previousSectionPromise.then((previousValue) => {
      if (previousValue !== undefined) {
        const summary = summarizeSectionDiff(previousValue, sectionValue, sectionKey);
        if (!summary) return;
        logActivity({
          kind: "deviceSection",
          payload: {
            deviceType: type,
            tabId,
            sectionKey,
            hostname: currentTab?.initial?.[0]?.hostname || "",
            summary
          }
        });
      } else {
        // First save for this tab — always audit, and call it out.
        logActivity({
          kind: "deviceSection",
          payload: {
            deviceType: type,
            tabId,
            sectionKey,
            hostname: currentTab?.initial?.[0]?.hostname || "",
            summary: `created ${sectionKey}`
          }
        });
      }
    }).catch(() => {});

    end();
    return result;
  } catch (error) {
    end(error);
    throw error;
  }
}

/**
 * Pull every server row of `type` for the active project, mirror them into
 * localStorage as `<type>_data`, and return the parsed tab list. Returns
 * null when there's no active project or no rows yet.
 *
 * @param {"router" | "switch" | "vlan"} type
 * @returns {Promise<object[] | null>}
 */
export async function syncdown(type) {
  const projectId = getProjectId();
  if (!projectId) return null;

  if (type === "vlan") {
    return syncPageDown("vlan");
  }

  if (!SUPPORTED_DEVICE_TYPES.includes(type)) return null;

  const rows = await listAllRows(tablesDB, {
    databaseId: APPWRITE_DATABASE_ID,
    tableId: PROJECT_DEVICES_TABLE_ID,
    queries: [
      Query.equal("projectId", [projectId]),
      Query.equal("deviceType", [type]),
      Query.orderAsc("deviceId")
    ]
  });

  if (!rows.length) return null;

  const tabs = rows.map((row, idx) =>
    parseJson(
      row.dataJson,
      type === ROUTER_TYPE ? getDefaultRouterTab(idx + 1) : getDefaultSwitchTab(idx + 1)
    )
  );
  localStorage.setItem(`${type}_data`, JSON.stringify(tabs));
  return tabs;
}

export async function syncupchange(tabId, sectionKey, sectionValue, type, nest, nestIndex) {
  if (!SUPPORTED_DEVICE_TYPES.includes(type)) return;

  const current = parseJson(localStorage.getItem(`${type}_data`), []);
  const activeTab = current?.[Number(tabId)];
  if (!activeTab) return;

  if (nest && Number.isInteger(nestIndex) && activeTab[nest]?.[nestIndex]) {
    await upsertDeviceRow(type, Number(tabId), activeTab);
    return;
  }

  if (sectionKey && sectionValue !== undefined) {
    await upsertDeviceRow(type, Number(tabId), activeTab);
    return;
  }

  await syncup(current, type);
}

export function subscribeSync(type, callback) {
  const projectId = getProjectId();
  if (!projectId) return () => {};

  if (type === "vlan") {
    return subscribePageSync("vlan", callback);
  }

  return client.subscribe(
    `databases.${APPWRITE_DATABASE_ID}.tables.${PROJECT_DEVICES_TABLE_ID}.rows`,
    (event) => {
      const row = event?.payload;
      if (!row) return;
      if (row.projectId !== projectId) return;
      if (row.deviceType !== type) return;

      // Appwrite broadcasts row deletes with the row's last `dataJson`
      // still attached. Without this flag the editor would treat a delete
      // as an update and re-insert the tab into local state.
      const events = Array.isArray(event?.events) ? event.events : [];
      const isDelete = events.some(
        (name) => typeof name === "string" && name.endsWith(".delete")
      );

      callback?.({
        tabId: Number(row.deviceId),
        tab: isDelete
          ? null
          : parseJson(
              row.dataJson,
              type === ROUTER_TYPE
                ? getDefaultRouterTab(Number(row.deviceId) + 1)
                : getDefaultSwitchTab(Number(row.deviceId) + 1)
            ),
        updatedAtMs: Number(row.updatedAtMs || 0),
        isDelete
      });
    }
  );
}

